// Cloud Backup renderer helpers, shared by the Settings section and
// AutoSyncManager's post-sync snapshot hook. Desktop only — callers must
// guard on `window.electron?.backup` (the web/native shims don't expose it).
import { track } from './analytics';

export const BACKUP_TOOLS = [
    { tool: 'rekordbox', label: 'Rekordbox' },
    { tool: 'serato', label: 'Serato' },
    { tool: 'traktor', label: 'Traktor' },
    { tool: 'virtualdj', label: 'Virtual DJ' },
];

export const backupToolLabel = (tool) =>
    BACKUP_TOOLS.find((t) => t.tool === tool)?.label || tool;

/**
 * Turn the enable sheet's picks into the settings patch applyBackupPatch
 * sends. The sheet's rule is "what you see ticked is what gets backed up":
 * every DETECTED tool is named explicitly in `tools`, because the stored map
 * defaults to all-true and patches merge shallowly — an omitted tool would
 * silently keep (or regain) an old value the sheet never showed. Undetected
 * tools are left alone; the engine skips them anyway. Unticking every app is
 * the same choice as unticking the layer. Pure — unit-tested.
 *
 * @param {{pickFiles:boolean,pickDj:boolean,pickTools?:Object}} picks
 * @param {Array<{tool:string}>} detectedTools
 * @returns {{patch:Object, djAppsSelected:number}}
 */
export function backupEnablePatch({ pickFiles, pickDj, pickTools }, detectedTools) {
    const picked = (pickDj && detectedTools.length)
        ? detectedTools.filter(({ tool }) => pickTools?.[tool] !== false)
        : [];
    const djOn = picked.length > 0;
    return {
        patch: {
            enabled: true,
            files: !!pickFiles,
            djData: djOn,
            ...(djOn
                ? { tools: Object.fromEntries(detectedTools.map(({ tool }) => [tool, pickTools?.[tool] !== false])) }
                : {}),
        },
        djAppsSelected: picked.length,
    };
}

/** Fire per-tool snapshot analytics for a backup:snapshot:run result set. */
export function trackSnapshotResults(results, from) {
    for (const r of results || []) {
        if (r.status === 'stored') {
            track('backup_snapshot_created', {
                tool: r.tool,
                total_bytes: r.totalBytes ?? 0,
                duration_ms: r.durationMs ?? 0,
                from,
            });
        } else if (r.status === 'failed') {
            track('backup_snapshot_failed', { tool: r.tool, reason: r.reason || r.error || 'unknown', from });
        }
    }
}

/**
 * What the storage meter should actually say. `bytesUsed` is what is STORED;
 * `reservedBytes` is quota the server is holding for files that have been
 * negotiated but not yet uploaded. The meter used to add the two together and
 * call the sum "used", which on a 3,500-track library read "58 GB of 100 GB
 * used" when 0.9 GB was actually in the cloud. Reserved bytes are real (they
 * count against the quota check) but they are PENDING, not used, and saying
 * otherwise makes the product look broken at exactly the moment it is working.
 */
export function backupStorage(usage) {
    if (!usage) return null;
    const stored = (usage.bytesUsed || 0) + (usage.snapshotBytes || 0);
    const pending = usage.reservedBytes || 0;
    const quota = usage.quotaBytes || 0;
    const pct = (n) => (quota ? Math.min(100, (n / quota) * 100) : 0);
    const storedPct = pct(stored);
    return {
        stored,
        musicBytes: usage.bytesUsed || 0,
        djBytes: usage.snapshotBytes || 0,
        pending,
        quota,
        // Only the stored share fills the bar. Pending rides behind it as a
        // ghost segment, so "nearly full" still warns before the quota bites
        // without pending being counted as space already spent.
        storedPct,
        pendingPct: Math.max(0, Math.min(100 - storedPct, pct(pending))),
        nearlyFull: quota ? (stored + pending) / quota >= 0.8 : false,
    };
}

/**
 * "4,516 files · 58.2 GB", or just "4,516 files" when we cannot honestly say.
 *
 * tracks.file_size is NULL until a track has been enriched, so on a fresh
 * library the sum covers a handful of rows. Showing it anyway described a 60GB
 * library as "30 MB". Below full coverage the size is simply omitted — a
 * missing number is recoverable, a wrong one by three orders of magnitude is
 * what someone agrees to an upload on.
 */
export function backupPreflightSummary(preview) {
    const count = preview?.count || 0;
    if (!count) return null;
    const files = `${count.toLocaleString()} ${count === 1 ? 'file' : 'files'}`;
    const sized = preview?.sized ?? count;
    const base = sized >= count ? `${files} · ${formatBytes(preview.bytes)}` : files;
    // The queue's own memory of what's uploaded. Quoting the full library
    // size at someone whose cloud copy is already complete reads as "this
    // will upload 31 GB" — say what will actually happen instead.
    //
    // The partial line states only what is KNOWN ("4,116 already backed up"),
    // never "N not backed up yet": the remainder is often library rows whose
    // files live on a detached drive — the run stats and skips them, so a
    // promised upload would never happen (measured live: 975 of 5,091 were
    // /Music/... paths from another volume).
    const stored = preview?.stored || 0;
    if (stored >= count) return `${base} — all already backed up`;
    if (stored > 0) return `${base} — ${stored.toLocaleString()} already backed up`;
    return base;
}

/** True when the files preview says the cloud copy is already complete. */
export function backupPreviewAllStored(preview) {
    return !!preview && !preview.loading && (preview.count || 0) > 0
        && (preview.stored || 0) >= preview.count;
}

export function formatBytes(bytes) {
    if (bytes == null || Number.isNaN(bytes)) return '—';
    if (bytes < 1024) return `${bytes} B`;
    const units = ['KB', 'MB', 'GB', 'TB'];
    let value = bytes / 1024;
    let unit = 0;
    while (value >= 1024 && unit < units.length - 1) { value /= 1024; unit++; }
    return `${value >= 10 ? Math.round(value) : value.toFixed(1)} ${units[unit]}`;
}

export function formatTimeAgo(ts) {
    if (!ts) return null;
    const mins = Math.max(0, Math.round((Date.now() - ts) / 60000));
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.round(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.round(hours / 24);
    return `${days}d ago`;
}

/**
 * "1,234 of 1,300 tracks backed up" from the local queue counts (stored vs
 * every state); null when the queue is empty or counts are unknown.
 */
export function backupFilesCountLine(status) {
    const counts = status?.files?.counts;
    if (!counts) return null;
    const total = Object.values(counts).reduce((a, b) => a + (b || 0), 0);
    if (!total) return null;
    const stored = counts.stored || 0;
    return `${stored.toLocaleString()} of ${total.toLocaleString()} track${total === 1 ? '' : 's'} backed up`;
}

/** Subtitle for the Settings "Music files" row (Stage B). */
export function backupFilesStatusLine(status) {
    const settings = status?.settings;
    const files = status?.files;
    const usage = status?.usage;
    if (files?.state === 'entitlement_paused') return 'Paused — GOAT required';
    if (!settings?.files) return 'Back up the audio files themselves';
    if ((files?.counts?.skipped_quota || 0) > 0) return 'Backup storage is full — some files are not backed up';
    if (files?.state === 'paused') return 'Paused';
    const countLine = backupFilesCountLine(status);
    if (files?.state === 'running') return countLine ? `Backing up… · ${countLine}` : 'Backing up…';
    if (countLine) {
        return usage?.quotaBytes
            ? `${countLine} · ${formatBytes(usage.bytesUsed)} stored`
            : countLine;
    }
    if (usage && usage.quotaBytes) {
        const count = usage.fileCount ?? 0;
        return `${formatBytes(usage.bytesUsed)} stored · ${count.toLocaleString()} ${count === 1 ? 'file' : 'files'}`;
    }
    return 'On';
}

/** Small "what's not backed up" line under the files row; null when clean. */
export function backupFilesIssueLine(status) {
    const counts = status?.files?.counts;
    if (!counts) return null;
    const parts = [];
    if (counts.failed > 0) parts.push(`${counts.failed.toLocaleString()} ${counts.failed === 1 ? 'file' : 'files'} couldn't be backed up`);
    if (counts.skipped_quota > 0) parts.push(`${counts.skipped_quota.toLocaleString()} skipped — storage full`);
    return parts.length ? parts.join(' · ') : null;
}

/**
 * Put files back where they came from, asking for a folder only for the ones
 * whose original location is gone — a deleted folder, or an external drive
 * that isn't plugged in.
 *
 * The folder applies ONLY to those strays (`destDirMode: 'orphans'`). Sending
 * the whole selection there because a few were orphaned flattened the user's
 * directory structure — their organisation — to rescue a handful of files.
 *
 * Returns the restore result, { cancelled: true }, or null when unavailable.
 */
export async function restoreFilesWithPrompt(items) {
    const backup = window.electron?.backup;
    if (!backup?.restoreFiles || !items?.length) return null;
    let res = await backup.restoreFiles({ items });
    if (res?.needsDestDir) {
        const strays = res.count || 0;
        const total = res.total || items.length;
        const picked = await window.electron.dialog.showOpenDialog({
            properties: ['openDirectory', 'createDirectory'],
            title: strays < total
                ? `Where should ${strays} file${strays === 1 ? '' : 's'} go?`
                : 'Choose where to restore your files',
            message: strays < total
                ? `${strays} of ${total} files can’t go back to their original folders — those folders are gone, or on a drive that isn’t connected. The rest will be restored where they were.`
                : 'Their original folders are gone, or on a drive that isn’t connected.',
        });
        const dir = picked?.filePaths?.[0];
        if (!dir) return { cancelled: true };
        res = await backup.restoreFiles({ items, destDir: dir, destDirMode: 'orphans' });
    }
    return res;
}

/**
 * Which face the Settings status hero shows, from a backup:getStatus result.
 * Priority (first match wins): off → paused_entitlement → backing_up →
 * attention → queued → protected. "protected" is EARNED: a run-level files
 * error (negotiate/network — no per-file rows failed) is attention, and
 * outstanding queue rows are queued — never "All backed up" with tracks
 * still waiting. Pure — unit-tested in cloudBackup.test.js.
 */
/**
 * Upload speed presets. Mirrors backupSettings.SPEEDS in the main process —
 * the engine is the authority on the actual rates; these are the labels.
 */
export const BACKUP_SPEED_OPTIONS = [
    { key: 'full', label: 'Full', hint: 'Use the whole connection' },
    { key: 'balanced', label: 'Balanced', hint: 'About 5 MB/s' },
    { key: 'background', label: 'Background', hint: 'About 1 MB/s, one file at a time' },
];

/** Snooze presets offered on the Stop menu, in the order they're shown. */
export const BACKUP_SNOOZE_OPTIONS = [
    { minutes: 60, label: 'Pause for 1 hour' },
    { minutes: 8 * 60, label: 'Pause for 8 hours' },
    { minutes: 24 * 60, label: 'Pause for a day' },
];

/** True while a snooze deadline is still in the future. */
export function isBackupSnoozed(settings, now = Date.now()) {
    return !!(settings?.snoozeUntil && settings.snoozeUntil > now);
}

/**
 * "Backups resume at 4:30 PM" / "…tomorrow at 9:15 AM". Says WHEN rather than
 * how long is left, so the line doesn't need a ticking re-render to stay true.
 */
export function backupSnoozeLine(settings, now = Date.now()) {
    if (!isBackupSnoozed(settings, now)) return null;
    const until = new Date(settings.snoozeUntil);
    const time = until.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
    const sameDay = until.toDateString() === new Date(now).toDateString();
    return sameDay ? `Backups resume at ${time}` : `Backups resume tomorrow at ${time}`;
}

/**
 * Per-LAYER state for the "what is protected" rows — one for DJ app data, one
 * for music files. The old row was a flat list of green ticks that claimed
 * everything was protected the moment backup was switched on, so a DJ whose
 * cues had never been snapshotted and whose 3,500 files were an hour from
 * done saw exactly the same thing as someone fully backed up.
 *
 * Each row is 'done' | 'syncing' | 'pending' | 'off':
 *   done    — green tick, this layer really is in the cloud
 *   syncing — blue dot, work in flight right now
 *   pending — grey, enabled but nothing has happened yet
 *   off     — the user turned this layer off; not shown
 *
 * Pure — unit-tested in cloudBackup.test.js.
 */
export function backupLayerRows(status, live = {}) {
    const settings = status?.settings;
    if (!settings?.enabled) return [];
    const rows = [];
    // `status` is polled; the progress stream is instant. Deriving these rows
    // from the poll alone left "Music files: Waiting" sitting under a hero that
    // was visibly uploading — the same panel disagreeing with itself for up to
    // a poll interval. Live flags win when they are set.
    const snapshotRunning = live.snapshotRunning ?? status?.snapshotRunning;
    const filesRunning = live.filesRunning || status?.files?.state === 'running';

    // ── DJ app data (fast, tiny, most irreplaceable — listed first) ──
    // BOTH layers are always listed, off ones included. Hiding a disabled
    // layer was why nobody could tell Cloud Backup covers cues, grids and
    // playlists at all: with djData off the panel never mentioned DJ data
    // existed, so the most valuable thing the feature protects was invisible
    // to the person deciding whether to use it. An 'off' row says "this exists
    // and you don't have it" — which is the whole point.
    if (settings.djData === false) {
        rows.push({
            key: 'dj',
            label: 'DJ app data',
            detail: 'Cues, grids & playlists',
            state: 'off',
        });
    } else {
        const tools = BACKUP_TOOLS.filter(({ tool }) => settings.tools?.[tool] !== false
            && status?.detected?.[tool]);
        const done = tools.filter(({ tool }) => {
            const last = settings.lastSnapshot?.[tool];
            return last && (last.status === 'stored' || last.status === 'unchanged');
        });
        const failed = tools.filter(({ tool }) => settings.lastSnapshot?.[tool]?.status === 'failed');
        rows.push({
            key: 'dj',
            label: 'DJ app data',
            detail: tools.length
                ? tools.map((t) => t.label).join(', ')
                : 'No DJ apps found on this computer',
            // 'none' rather than 'pending' when there is nothing installed to
            // snapshot. The layer is on and correct, there is simply no work —
            // reporting that as "Waiting" implied a backup was coming that
            // never would.
            state: !tools.length ? 'none'
                : snapshotRunning ? 'syncing'
                    : failed.length ? 'attention'
                        : done.length === tools.length ? 'done' : 'pending',
            // The snapshot pass is a change CHECK until something actually
            // uploads (export → hash → compare, upload only on drift). Saying
            // "Backing up…" over an unchanged library reads as re-uploading
            // work that is already safe — label the check as a check. After
            // Stop, the pass still runs to completion (it has no abort path),
            // but "Checking for changes…" next to a hero saying "Stopping…"
            // read as the click having been ignored — agree with the hero.
            ...(snapshotRunning && live.snapshotUploading !== true
                ? { syncingLabel: live.stopRequested ? 'Finishing check…' : 'Checking for changes…' } : {}),
        });
    }

    // ── Music files (slow, huge — the one with a live count) ──
    if (settings.files !== true) {
        rows.push({
            key: 'files',
            label: 'Music files & tags',
            detail: 'The audio files themselves',
            state: 'off',
        });
    } else {
        const counts = status?.files?.counts;
        const polledTotal = counts ? Object.values(counts).reduce((a, b) => a + (b || 0), 0) : 0;
        const outstanding = counts
            ? (counts.pending || 0) + (counts.hashing || 0) + (counts.ready || 0) + (counts.uploading || 0)
            : 0;
        // While a run is live the engine's stream is ahead of the polled queue
        // snapshot (dedupe can store several rows per upload), and the hero
        // already shows the live number — so this row showed 36 next to a hero
        // saying 39 for up to a poll interval. Live pair wins, and it is taken
        // as a PAIR: mixing live stored into the polled total (or vice versa)
        // can read "40 of 36".
        const liveCount = live.libraryTotal > 0;
        const total = liveCount ? live.libraryTotal : polledTotal;
        const stored = liveCount ? Math.min(live.libraryDone || 0, live.libraryTotal) : (counts?.stored || 0);
        rows.push({
            key: 'files',
            label: 'Music files & tags',
            detail: total ? `${stored.toLocaleString()} of ${total.toLocaleString()}` : null,
            state: filesRunning ? 'syncing'
                : !total ? 'pending'
                    : outstanding > 0 ? 'pending'
                        : (counts?.failed || 0) > 0 ? 'attention' : 'done',
        });
    }

    return rows;
}

/**
 * A stored backup error, worded for the person reading it. The engine keeps
 * the raw message — it is what the analytics `reason` and a bug report need —
 * but "self signed certificate in certificate chain" on a Settings row tells a
 * DJ nothing they can act on. The recognised shapes share one cause, a
 * network that intercepts or blocks the storage host, and one remedy: another
 * network. Anything unrecognised passes through unchanged. Pure.
 */
export function describeBackupError(error) {
    const raw = String(error || '').trim();
    if (!raw) return 'Upload failed';
    if (/self.signed certificate|SELF_SIGNED_CERT|UNABLE_TO_VERIFY_LEAF_SIGNATURE|CERT_UNTRUSTED|unable to get local issuer certificate/i.test(raw)) {
        return 'Blocked by a network proxy — try another network';
    }
    if (/blocked before reaching storage/i.test(raw)) {
        return 'Blocked by a network policy — try another network';
    }
    if (/ECONNRESET|ETIMEDOUT|ENOTFOUND|EAI_AGAIN|ECONNREFUSED|ENETUNREACH|timed out|socket hang up/i.test(raw)) {
        return 'Connection dropped';
    }
    return raw;
}

/** "Rekordbox" · "Rekordbox and Serato" · "Rekordbox, Serato and Traktor". */
export function joinNames(names) {
    const list = (names || []).filter(Boolean);
    if (list.length <= 1) return list[0] || '';
    return `${list.slice(0, -1).join(', ')} and ${list[list.length - 1]}`;
}

/**
 * The DJ-data failures behind an attention hero: one entry per enabled,
 * detected tool whose LAST snapshot attempt failed, with the raw error and
 * its user-facing wording. The hero, the failure review and Retry all read
 * from this one list — before it existed the hero named the failure from
 * lastSnapshot while the review listed only files-queue rows, so a failed
 * Rekordbox snapshot opened "Nothing left to review" under "Rekordbox backup
 * failed", and Retry all retried zero files. Off layer = nothing will ever
 * retry, so a stale failure under it is not an issue either. Pure.
 */
export function backupSnapshotIssues(status) {
    const settings = status?.settings;
    if (!settings?.enabled || settings.djData === false) return [];
    return BACKUP_TOOLS
        .filter(({ tool }) => settings.tools?.[tool] !== false
            && status?.detected?.[tool]
            && settings.lastSnapshot?.[tool]?.status === 'failed')
        .map(({ tool, label }) => {
            const last = settings.lastSnapshot[tool];
            return { tool, label, at: last.at, error: last.error || null, reason: describeBackupError(last.error) };
        });
}

export function deriveBackupHeroState(status) {
    const settings = status?.settings;
    if (!settings?.enabled) return 'off';
    // FROZEN outranks everything below it. The entitlement lapsed: nothing new
    // can go up, everything already up is still there, and there is a deletion
    // date. That is the single most important thing the panel can say, and
    // until this state existed the app said nothing at all — a lapsed trial
    // just quietly stopped working and, later, quietly lost its data.
    if (status?.usage?.frozen) return 'frozen';
    if (settings.filesEntitlementPaused || status?.files?.state === 'entitlement_paused') return 'paused_entitlement';
    if (status?.snapshotRunning || status?.files?.state === 'running') return 'backing_up';
    // The user's own Stop outranks queue/issue states: they know files are
    // outstanding — that's what stopping means. It stays until they resume.
    if (settings.filesPaused || status?.files?.state === 'paused') return 'paused';
    // Snooze ranks just under Stop and above everything the user did not ask
    // for: they said "not now", so queued rows are expected, not a problem.
    if (isBackupSnoozed(settings) || status?.files?.state === 'snoozed') return 'snoozed';
    // Held off by a metered connection — an explicit, user-configured wait,
    // so it must not read as "attention" or a stalled queue.
    if (status?.files?.state === 'waiting_network') return 'waiting_network';
    if ((status?.issuesCount || 0) > 0 || backupSnapshotIssues(status).length > 0) return 'attention';
    if (settings.files === true && settings.lastFilesRun?.error) return 'attention';
    const counts = status?.files?.counts;
    const outstanding = counts
        ? (counts.pending || 0) + (counts.hashing || 0) + (counts.ready || 0) + (counts.uploading || 0)
        : 0;
    if (settings.files === true && outstanding > 0) return 'queued';
    return 'protected';
}

/**
 * Live "X of Y tracks backed up" from a backup:progress event, with the stage
 * the engine is in. The engine's own done/total are stage-scoped (files being
 * hashed, then files being uploaded) — the library-wide pair is what answers
 * "how many of mine are safe", and it's populated from the FIRST event, so
 * the long hashing pass is no longer a count-less "Backing up…".
 */
export function backupProgressLine(p) {
    if (!p || p.libraryTotal == null || !p.libraryTotal) return null;
    const done = Math.min(p.libraryDone || 0, p.libraryTotal);
    return `${done.toLocaleString()} of ${p.libraryTotal.toLocaleString()} track${p.libraryTotal === 1 ? '' : 's'} backed up`;
}

/**
 * Percent toward a completion claim, for progress bars. FLOORS instead of
 * rounding: "100%" must mean done — 4,108 of 4,116 rounded up to a full bar
 * with uploads still running, which reads as finished-but-stuck. Flooring
 * makes 100 reachable only when done === total.
 */
export function completionPct(done, total) {
    if (!total || total <= 0) return null;
    return Math.min(100, Math.floor(((done || 0) / total) * 100));
}

/** Percent complete of the whole library (not of the current run's bytes). */
export function backupProgressPct(p) {
    if (!p?.libraryTotal) return null;
    return completionPct(p.libraryDone, p.libraryTotal);
}

/**
 * What the engine is doing right now, for the line under the count.
 *
 * `p` is the whole progress event, because "Uploading" is not the whole truth:
 * a file on its second or third attempt is being RETRIED, and saying so is the
 * difference between a name that looks stuck in a loop and one that explains
 * itself. Two uploads run in parallel, so the count goes on the line too —
 * otherwise a name changing looks like the previous file finished, when it is
 * simply the other worker's turn to report.
 */
export function backupStageLabel(stageOrProgress) {
    const p = typeof stageOrProgress === 'string' ? { stage: stageOrProgress } : (stageOrProgress || {});
    switch (p.stage) {
        case 'hashing': return 'Checking your files';
        case 'checking': return 'Checking what’s already backed up';
        case 'uploading':
            return (p.currentAttempt || 1) > 1
                ? `Retrying (${p.currentAttempt} of ${p.maxAttempts || 3})`
                : 'Uploading';
        default: return 'Preparing';
    }
}

/**
 * "4 files at once" while uploads run in parallel; null when there is only one.
 *
 * Says the TOTAL, not "+3 more". The featured file is one of several, and the
 * total is what explains why the name changes without the previous file being
 * finished — "+3 more" made the reader do arithmetic to find that out.
 */
export function backupInFlightLabel(p) {
    const n = p?.inFlight || 0;
    if (p?.stage !== 'uploading' || n < 2) return null;
    return `${n} files at once`;
}

/**
 * What the account already holds in the cloud, from a backup:restore:preview
 * result: null when the cloud is empty (or the preview failed), otherwise the
 * facts a "your backup exists" line needs. Shared by RestoreFoundPrompt and
 * the off-state hero — backup on/off is a PER-MACHINE switch, and a hero
 * shouting "Not protected — nothing is backed up" account-wide while the
 * restore dialog two clicks away lists last night's snapshots is the panel
 * calling itself a liar. Pure — unit-tested in cloudBackup.test.js.
 */
export function summarizeCloudBackup(preview) {
    if (!preview || preview.error) return null;
    const snapshots = [...(preview.tools || []), ...(preview.model ? [preview.model] : [])];
    const filesTotal = preview.files?.total || 0;
    if (!snapshots.length && !filesTotal) return null;
    const lastAt = snapshots
        .map((s) => new Date(s.at).getTime())
        .filter((t) => Number.isFinite(t))
        .sort((a, b) => b - a)[0] || null;
    return {
        toolCount: (preview.tools || []).length,
        playlists: preview.model?.counts?.playlists ?? null,
        filesTotal,
        hostname: preview.model?.hostname || preview.tools?.[0]?.hostname || null,
        lastAt,
    };
}

/**
 * The lopsided-backup warning for the restore wizard's scope screen: the DJ
 * snapshots reference far more tracks than the cloud holds music files for —
 * the files layer was off, blocked, or still uploading on the machine that
 * made the backup. Without this the screen puts "4,609 tracks in the backup"
 * next to "180 backed-up files" and leaves the reader to conclude the product
 * is broken (or worse, that everything is coming back). Null under a 10%
 * shortfall: enrichment drift and detached-drive rows make small gaps normal,
 * and a warning that always shows is one nobody reads. Pure — unit-tested.
 */
/**
 * True when a re-merge of `tool` would add nothing: this machine already
 * merged what the backup holds for it, and no music has arrived locally
 * since (fewer missing files than at merge time would let previously-dropped
 * playlists survive a re-merge). The wizard then shows a status line and
 * defaults the app unchecked instead of re-offering the same merge forever.
 *
 * Two ways to be current. The exact portable-model snapshot already merged
 * is the plain one. But that model is ONE file, rebuilt whenever ANY app
 * changes — so adding one 7-track Traktor playlist re-armed Rekordbox,
 * Serato and Virtual DJ too, and the wizard re-listed (and re-ticked) every
 * app's whole library. Each app also has its own sha-gated snapshot, so an
 * app is current when its own snapshot is the one recorded at merge time,
 * the merged model already post-dated that snapshot (the model uploads
 * after the app's snapshot in the same pass), and the model's counts for it
 * haven't moved (the counts come from the model itself, so a stale app
 * snapshot — its layer switched off — can't vouch for a model that grew).
 * `entry` = settings.lastMergeDj[tool] (see mergeRecordEntry). Pure — unit-tested.
 */
export function mergeUpToDate(entry, preview, tool) {
    if (!entry || !preview?.model) return false;
    if ((preview.files?.missing ?? 0) < (entry.filesMissing ?? 0)) return false;
    if (entry.snapshotId === preview.model.snapshotId) return true;
    if (!tool || !entry.toolSnapshotId || !entry.modelAt || !entry.counts) return false;
    const own = (preview.tools || []).find((t) => t.tool === tool);
    if (!own || own.snapshotId !== entry.toolSnapshotId) return false;
    const ownAt = Date.parse(own.at);
    const mergedModelAt = Date.parse(entry.modelAt);
    if (!Number.isFinite(ownAt) || !Number.isFinite(mergedModelAt) || ownAt > mergedModelAt) return false;
    const now = preview.model.counts?.perTool?.[tool];
    return !!now && now.tracks === entry.counts.tracks && now.playlists === entry.counts.playlists;
}

/**
 * The settings.lastMergeDj[tool] entry to record after a merge: which model
 * snapshot, when, how much music was still missing — plus the per-app
 * markers mergeUpToDate compares next time (the app's own snapshot id, when
 * the model was stored, the model's counts for it), each omitted when the
 * preview can't supply it so the plain snapshot-id rule still applies.
 * Pure — unit-tested.
 */
export function mergeRecordEntry(preview, tool, { snapshotId, filesMissing, at }) {
    const own = (preview?.tools || []).find((t) => t.tool === tool);
    const counts = preview?.model?.counts?.perTool?.[tool];
    return {
        snapshotId: snapshotId || preview?.model?.snapshotId || null,
        at,
        filesMissing,
        ...(own?.snapshotId ? { toolSnapshotId: own.snapshotId } : {}),
        ...(preview?.model?.at ? { modelAt: preview.model.at } : {}),
        ...(counts ? { counts: { tracks: counts.tracks || 0, playlists: counts.playlists || 0 } } : {}),
    };
}

/**
 * What changed for an app since this machine last merged it, for the wizard
 * row: "+1 playlist · +7 tracks since you merged 2h ago". Growth is what an
 * ADD-ONLY merge can bring, so only positive deltas are named; a change that
 * didn't grow the counts (an edit, a rename, cues) is just "Changed". Null
 * without a prior merge, or without counts on either side. Pure — unit-tested.
 *
 * @returns {{text: string, grown: boolean}|null}
 */
export function mergeDeltaLine(entry, counts, ago) {
    if (!entry?.counts || !counts) return null;
    const dp = (counts.playlists || 0) - (entry.counts.playlists || 0);
    const dt = (counts.tracks || 0) - (entry.counts.tracks || 0);
    const since = `since you merged ${ago}`;
    const parts = [];
    if (dp > 0) parts.push(`+${dp.toLocaleString()} ${dp === 1 ? 'playlist' : 'playlists'}`);
    if (dt > 0) parts.push(`+${dt.toLocaleString()} ${dt === 1 ? 'track' : 'tracks'}`);
    if (parts.length) return { text: `${parts.join(' · ')} ${since}`, grown: true };
    return { text: `Changed ${since}`, grown: false };
}

export function restoreCoverageGap(preview) {
    const modelTracks = preview?.model?.counts?.tracks || 0;
    const filesTotal = preview?.files?.total || 0;
    const libraryTracks = preview?.model?.counts?.libraryTracks;
    if (!modelTracks) return null;
    const known = typeof libraryTracks === 'number';
    // The music-file backup covers the source machine's MixGoat library, not
    // its DJ apps' collections. With the library count in the snapshot meta
    // the two shortfalls separate: tracks never imported into MixGoat (by
    // design — nothing was "off"), and files the backup is genuinely behind
    // on. Older snapshots carry no library count; then only the DJ-app union
    // is comparable and the note has to name both possibilities.
    const onlyInDjApps = known && libraryTracks < modelTracks * 0.9;
    const behind = known ? filesTotal < libraryTracks * 0.9 : filesTotal < modelTracks * 0.9;
    if (!onlyInDjApps && !behind) return null;
    const reason = !known ? 'unknown' : (onlyInDjApps && behind ? 'both' : (onlyInDjApps ? 'not_imported' : 'behind'));
    return { modelTracks, filesTotal, libraryTracks: known ? libraryTracks : null, reason };
}

const BEHIND_CLAUSE = 'music-file backup was off, interrupted or still running on the computer that made it';
const NOT_IMPORTED_CLAUSE = 'Tracks that live only in a DJ app aren’t uploaded until you import them into MixGoat.';
const RESTORE_TAIL = 'Playlists and cues come back in full; music that was never uploaded can’t.';

/**
 * The sentence for a restoreCoverageGap() result. The first clause names the
 * ACTUAL reason for the gap when the snapshot lets us know it — "not
 * imported into MixGoat" is the common case and is by design, and leading
 * with "off / interrupted" there made a working backup read as broken.
 * Pure — unit-tested.
 */
export function restoreCoverageNote(gap) {
    if (!gap) return null;
    const n = (v) => v.toLocaleString();
    const files = gap.filesTotal > 0 ? `${n(gap.filesTotal)} music files` : 'no music files';
    switch (gap.reason) {
        case 'not_imported':
            return `Your DJ apps reference ${n(gap.modelTracks)} tracks, but music files are backed up only for your MixGoat library (${n(gap.libraryTracks)} tracks, ${files}). ${NOT_IMPORTED_CLAUSE} ${RESTORE_TAIL}`;
        case 'behind':
            return `Your MixGoat library holds ${n(gap.libraryTracks)} tracks, but the backup holds ${files} — ${BEHIND_CLAUSE}. ${RESTORE_TAIL}`;
        case 'both':
            return `Your DJ apps reference ${n(gap.modelTracks)} tracks; music files are backed up only for your MixGoat library (${n(gap.libraryTracks)} tracks), and the backup holds ${files} of those — ${BEHIND_CLAUSE}. ${NOT_IMPORTED_CLAUSE} ${RESTORE_TAIL}`;
        default:
            return `Your DJ app backups reference ${n(gap.modelTracks)} tracks, but the backup holds ${files}. Music files are backed up only for tracks in your MixGoat library — the rest live only in your DJ apps, or ${BEHIND_CLAUSE}. ${RESTORE_TAIL}`;
    }
}

/**
 * The explanatory line under a per-app merge result, or null when the plain
 * "N playlists and M tracks merged" needs no footnote. Two things it owes the
 * user (both learned from a real restore that said "Serato: 1 playlists"
 * against a wizard that promised 403):
 *
 * - The shortfall ratio: only playlists with at least one locally-present
 *   music file survive the merge, so "1 of 403 had their music here" is the
 *   difference between a bug report and an accurate expectation.
 * - The empty target: an app whose backed-up playlists all died in the
 *   local-file filter merges NOTHING (the cross-tool fallback that used to
 *   fill it with other apps' playlists is gone — MG-1018), and the note
 *   must say why the row reads "0 playlists merged".
 *
 * Pure — unit-tested in cloudBackup.test.js.
 */
export function mergeRestoreNote({ target, ownInBackup, ownSurvived, missingOwnTracks, missingNeverBackedUp }) {
    if (ownInBackup == null) return null; // older result shape — say nothing
    const label = backupToolLabel(target);
    // "Never backed up" is a different truth from "not restored yet": the
    // first can only be fixed on the machine that has the files (they live
    // outside the backed-up folders/drives), the second by restoring music
    // here. Saying "they come back once their files do" for the first is a
    // promise the restore can never keep.
    const neverLine = missingNeverBackedUp > 0
        ? ` ${missingNeverBackedUp.toLocaleString()} of the missing tracks ${missingNeverBackedUp === 1 ? 'was' : 'were'} never backed up — back ${missingNeverBackedUp === 1 ? 'it' : 'them'} up on the computer that has ${missingNeverBackedUp === 1 ? 'it' : 'them'} first.`
        : '';
    if (ownInBackup === 0) {
        return `The backup holds no ${label} playlists.`;
    }
    if (ownSurvived === 0) {
        if (missingOwnTracks != null && missingNeverBackedUp === missingOwnTracks && missingOwnTracks > 0) {
            return `None of your ${label} playlists’ music was ever backed up — it lives outside the folders and drives your backup covers. Back it up on the original computer first.`;
        }
        return `None of your ${label} playlists’ music is on this computer yet — restore your music files, then run this again.${neverLine}`;
    }
    if (ownSurvived < ownInBackup) {
        return `${ownSurvived.toLocaleString()} of ${ownInBackup.toLocaleString()} backed-up ${label} playlists had their music on this computer — the rest come back once their files do.${neverLine}`;
    }
    // Every playlist survived, but thinner than the backup holds — without
    // this line a playlist quietly missing a few tracks reads as a complete
    // restore ("will the user know?" — no, unless it says so here).
    if (missingOwnTracks > 0) {
        return `Its playlists came back, but ${missingOwnTracks.toLocaleString()} ${missingOwnTracks === 1 ? 'track' : 'tracks'} whose ${missingOwnTracks === 1 ? 'file isn’t' : 'files aren’t'} on this computer ${missingOwnTracks === 1 ? 'was' : 'were'} left out.${neverLine || ' Restore your music files, then run this again to fill them in.'}`;
    }
    return null;
}

/**
 * The wizard card's per-app availability line, or null when there is nothing
 * to warn about (or the preview couldn't compute availability). The card
 * otherwise promises "1 playlist · 27 tracks in the backup" for an app whose
 * music was never backed up, and the restore then silently brings nothing
 * (MG-1018 follow-up — "set final traktor" lived on an unbacked-up drive).
 * Alarm tone ONLY when the whole app is unavailable: ticking it would be a
 * guaranteed no-op. Pure — unit-tested in cloudBackup.test.js.
 *
 * @param {{tracks:number, unavailable:number, playlists:number, playlistsUnavailable:number}|null|undefined} avail
 * @returns {{text: string, alarm: boolean}|null}
 */
export function availabilityNote(avail) {
    if (!avail || !avail.unavailable) return null;
    if (avail.tracks > 0 && avail.unavailable === avail.tracks) {
        return {
            alarm: true,
            text: 'None of its music is in the backup — nothing can come back here until those files are backed up on the original computer.',
        };
    }
    const pl = avail.playlistsUnavailable > 0
        ? ` — ${avail.playlistsUnavailable.toLocaleString()} ${avail.playlistsUnavailable === 1 ? 'playlist has' : 'playlists have'} none of their music backed up`
        : '';
    return {
        alarm: false,
        text: `${avail.unavailable.toLocaleString()} of ${avail.tracks.toLocaleString()} tracks aren’t in the backup’s music files${pl}.`,
    };
}

/**
 * Should the one-time "We found your backup" prompt appear? Only for a
 * signed-in user with backup OFF, cloud data to restore, and no prior
 * dismissal. Pure — the caller supplies the fetched snapshot/file facts.
 */
export function shouldOfferRestore({ signedIn, settings, snapshots, filesTotal }) {
    if (!signedIn || !settings) return false;
    if (settings.enabled) return false;
    if (settings.restorePromptDismissed) return false;
    return (snapshots?.length || 0) > 0 || (filesTotal || 0) > 0;
}

/**
 * Should the "Your restore didn't finish" prompt appear? Only when a guided
 * restore STARTED (restorePending — durable, so it survives a mid-run crash)
 * and backed-up files are still missing on this machine. Independent of
 * restorePromptDismissed: that flag was set by the accept that started the
 * very restore that didn't finish. Pure — the caller supplies the fetched
 * missing count and clears restorePending itself when missing is 0.
 */
export function shouldOfferResume({ signedIn, settings, missing }) {
    if (!signedIn || !settings) return false;
    if (!settings.restorePending) return false;
    return (missing || 0) > 0;
}

/**
 * Should the "turn on Cloud Backup" offer appear at this trigger moment
 * (`library_sync` | `dj_sync` | `upgrade`)? Never for enabled backup. The two
 * sync moments share one durable dismissal; the upgrade moment gets a single
 * re-offer of its own (the user now owns the feature they may have been
 * gated out of), and its dismissal also silences future sync offers. Pure —
 * the caller supplies the fetched settings.
 */
export function shouldOfferBackup({ signedIn, settings, trigger }) {
    if (!signedIn || !settings) return false;
    if (settings.enabled) return false;
    if (trigger === 'upgrade') return !settings.backupUpgradeOfferDismissed;
    return !settings.backupOfferDismissed && !settings.backupUpgradeOfferDismissed;
}

/**
 * The Library's one-line Cloud Backup notice — "4,116 tracks aren't backed
 * up" — in the Files-header slot the live progress line uses.
 * It exists because a user who never turned backup on learns the feature
 * exists only from the Backup page or the post-sync offer prompt; the
 * Library is where they live. One sentence with the count, NOT a column of
 * 4,000 identical slash glyphs (that was considered and rejected — a status
 * column for an off feature has no per-row information).
 *
 * Shown to a signed-in user whose backup is OFF on this machine (the master
 * switch — a DJ-data-only setup is a deliberate choice, not a gap), until
 * they hide it (durable per account: `backupLibraryLineDismissed`) or turn
 * backup on. Never while a run or restore is reporting (the progress line
 * owns the slot) and never for a library with no local tracks. Pure.
 */
export function shouldShowLibraryBackupLine({ signedIn, settings, localTracks, running }) {
    if (!signedIn || !settings) return false;
    if (settings.enabled) return false;
    if (settings.backupLibraryLineDismissed) return false;
    if (running) return false;
    return Number(localTracks) > 0;
}

/**
 * Copy for that line: the fact, then the action. Free users get "part of
 * GOAT" (the click opens the wall, which names the count); anyone entitled
 * gets "Turn on" (the click opens the Backup page).
 */
export function libraryBackupLineCopy({ localTracks, canAccess }) {
    const n = Math.max(0, Number(localTracks) || 0);
    // Short on purpose: the line shares the Files header with its centered
    // title and must fit in the right half at the usual three-panel width.
    const status = n === 1
        ? '1 track isn\'t backed up'
        : `${n.toLocaleString()} tracks aren't backed up`;
    const action = canAccess ? 'Turn on Cloud Backup' : 'Cloud Backup is part of GOAT';
    return { status, action };
}

/** Truncate long file names from the middle, keeping the extension visible. */
export function middleEllipsis(name, max = 44) {
    if (!name || name.length <= max) return name;
    const head = Math.ceil((max - 1) / 2);
    const tail = Math.floor((max - 1) / 2);
    return `${name.slice(0, head)}…${name.slice(name.length - tail)}`;
}

/** One status line for a tool row in Settings. */
export function backupToolStatusLine(tool, detected, last) {
    if (!detected) return 'Not detected';
    if (!last) return 'Not backed up yet';
    if (last.status === 'stored') return `Backed up ${formatTimeAgo(last.at)}`;
    if (last.status === 'unchanged') return `Up to date (checked ${formatTimeAgo(last.at)})`;
    if (last.status === 'skipped_running') return `Skipped — ${backupToolLabel(tool)} is open`;
    if (last.status === 'failed') return `Failed — ${describeBackupError(last.error)}`;
    return 'Not backed up yet';
}
